// ============================================
// ROLC OneROLC - Login Page
// Version: 3.6.0
// ============================================

import { $, setContent } from '../utils/domHelpers.js';
import { escapeHtml, showToast, normalizeLastName } from '../utils.js';
import { login, quickLogin, forgotPin, checkPinStatus, searchMembers } from '../auth/authService.js';
import { navigateToHome } from '../router/routes.js';
import { isAuthenticated } from '../auth/authService.js';

// ============================================
// Render Login Page
// ============================================
export async function renderLoginPage(params = {}) {
    const token = localStorage.getItem('rolc_token');
    const savedName = localStorage.getItem('rolc_name');

    if (token && savedName) {
        renderQuickLogin(savedName);
    } else {
        renderFullLogin();
    }
}

// ============================================
// Render Full Login (3-step: Last Name → Select Name → PIN)
// ============================================
function renderFullLogin() {
    setContent(`
        <div class="card">
            <h2 class="card-title">Member Login</h2>
            <p class="card-subtitle" id="loginSubtitle" style="font-size:0.8rem;margin-top:4px;line-height:1.2;">Please enter your last name to get started.</p>

            <div class="form-group">
                <label for="lastName" id="lastNameLabel">Last Name</label>
                <input type="text" id="lastName" placeholder="e.g. Smith" autocomplete="family-name">
            </div>
            <button class="btn btn-primary" id="btnSearch">Search</button>

            <div id="nameSelectionSection" class="hidden" style="margin-top: 20px;">
                <label for="nameSelect" id="nameSelectLabel">Select Your Name</label>
                <select id="nameSelect" style="width: 100%; padding: 12px; font-size: 1rem; border-radius: 8px; border: 2px solid #d1d5db;">
                    <option value="">-- Pick your name --</option>
                </select>
            </div>

            <div id="pinSection" class="hidden" style="margin-top: 20px;">
                <label for="pinInput" id="pinLabel" style="font-size: 1.1rem; font-weight: 600;">4-Digit PIN</label>
                <div style="position: relative; display: flex; align-items: center;">
                    <input type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4" id="pinInput" placeholder="    " autocomplete="off"
                        style="font-size: 1.8rem; text-align: center; letter-spacing: 8px; padding: 16px 56px 16px 16px; height: 64px; border-radius: 12px; border: 2px solid #d1d5db; width: 100%; box-sizing: border-box;">
                    <button type="button" id="btnTogglePin" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; font-size: 1.3rem; cursor: pointer; padding: 8px;" title="Show/Hide PIN">👁</button>
                </div>
                <p style="font-size: 0.8rem; line-height: 1.2; color: #6b7280; margin-top: 8px;">Numbers only</p>
            </div>

            <div id="loginError" class="hidden" style="margin-top: 16px; padding: 12px; background: #fee2e2; border: 1px solid #dc2626; border-radius: 8px; text-align: center; color: #dc2626;"></div>

            <div id="pinButtonArea" class="hidden" style="margin-top: 16px;">
                <button class="btn btn-primary" id="btnLogin" style="font-size: 1.1rem; padding: 14px; width: 100%;">Login</button>
            </div>

            <div style="text-align: center; margin-top: 20px;">
                <a href="https://m.me/r.quilates.jr" target="_blank" rel="noopener noreferrer" style="color: #4E8EF7; font-size: 0.8rem; line-height: 1.2; text-decoration: underline; cursor: pointer; opacity: 0.9;">Having trouble logging in? Contact your admin</a>
            </div>
        </div>
    `);

    setupLoginHandlers();
}

// ============================================
// Render Quick Login (PIN only)
// ============================================
function renderQuickLogin(savedName) {
    setContent(`
        <div class="card">
            <h2 class="card-title">Welcome Back</h2>
            <p class="card-subtitle">Enter your 4-digit PIN to continue as <strong>${escapeHtml(savedName)}</strong>.</p>

            <div id="qlError" class="hidden" style="margin-top: 16px; padding: 12px; background: #fee2e2; border: 1px solid #dc2626; border-radius: 8px; text-align: center; color: #dc2626;"></div>

            <div style="margin-top: 20px;">
                <label for="qlPinInput" style="font-size: 1.1rem; font-weight: 600;">4-Digit PIN</label>
                <div style="position: relative; display: flex; align-items: center;">
                    <input type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4" id="qlPinInput" placeholder="    " autocomplete="off"
                        style="font-size: 1.8rem; text-align: center; letter-spacing: 8px; padding: 16px 56px 16px 16px; height: 64px; border-radius: 12px; border: 2px solid #d1d5db; width: 100%; box-sizing: border-box;">
                    <button type="button" id="qlTogglePin" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; font-size: 1.3rem; cursor: pointer; padding: 8px;" title="Show/Hide PIN">👁</button>
                </div>
                <p style="font-size: 0.8rem; line-height: 1.2; color: #6b7280; margin-top: 8px;">Numbers only</p>
            </div>

            <button class="btn btn-primary" id="qlSubmit" style="margin-top: 20px; font-size: 1.1rem; padding: 14px; width: 100%;">Login</button>

            <div style="text-align: center; margin-top: 16px; display: flex; flex-direction: column; gap: 12px;">
                <a href="#" id="qlUseFullLogin" style="color: #4E8EF7; font-size: 0.8rem; line-height: 1.2; text-decoration: underline; cursor: pointer; opacity: 0.9;">Not you? Log in with your name instead</a>
                <a href="https://m.me/r.quilates.jr" target="_blank" rel="noopener noreferrer" style="color: #4E8EF7; font-size: 0.8rem; line-height: 1.2; text-decoration: underline; cursor: pointer; opacity: 0.9;">Having trouble logging in? Contact your admin</a>
            </div>
        </div>
    `);

    setupQuickLoginHandlers(savedName);
}

// ============================================
// Utilities
// ============================================
function scrollToCenter(el) {
    if (!el) return;
    const isMobile = window.innerWidth < 768;
    const offset = isMobile ? window.innerHeight * 0.25 : window.innerHeight * 0.35;
    
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - offset;
    
    try {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
    } catch (e) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

// ============================================
// Setup Login Handlers
// ============================================
function setupLoginHandlers() {
    const lastNameInput = document.getElementById('lastName');
    const btnSearch = document.getElementById('btnSearch');
    const nameSelect = document.getElementById('nameSelect');
    const pinInput = document.getElementById('pinInput');
    const btnLogin = document.getElementById('btnLogin');
    const loginError = document.getElementById('loginError');

    let selectedFullName = '';
    let selectedLastName = '';
    let isFirstTime = false;

    function updateProgress(step) {
        const setBadge = (id, bg, border, color) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.style.background = bg;
            el.style.borderColor = border;
            el.style.color = color;
        };
        const setLabel = (id, color) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.style.color = color;
        };

        if (step === 1) {
            setBadge('step1Badge', '#3b82f6', '#3b82f6', 'white');
            setLabel('step1Label', '#3b82f6');
            setBadge('step2Badge', '#e5e7eb', '#e5e7eb', '#9ca3af');
            setLabel('step2Label', '#9ca3af');
            setBadge('step3Badge', '#e5e7eb', '#e5e7eb', '#9ca3af');
            setLabel('step3Label', '#9ca3af');
        } else if (step === 2) {
            setBadge('step1Badge', '#10b981', '#10b981', 'white');
            setLabel('step1Label', '#059669');
            setBadge('step2Badge', '#3b82f6', '#3b82f6', 'white');
            setLabel('step2Label', '#3b82f6');
            setBadge('step3Badge', '#e5e7eb', '#e5e7eb', '#9ca3af');
            setLabel('step3Label', '#9ca3af');
        } else if (step === 3) {
            setBadge('step1Badge', '#10b981', '#10b981', 'white');
            setLabel('step1Label', '#059669');
            setBadge('step2Badge', '#10b981', '#10b981', 'white');
            setLabel('step2Label', '#059669');
            setBadge('step3Badge', '#3b82f6', '#3b82f6', 'white');
            setLabel('step3Label', '#3b82f6');
        }
    }

    function showError(msg, isSuccess = false) {
        loginError.textContent = msg;
        loginError.classList.remove('hidden');
        if (isSuccess) {
            loginError.style.background = '#dcfce7';
            loginError.style.border = '1px solid #16a34a';
            loginError.style.color = '#166534';
        } else {
            loginError.style.background = '#fee2e2';
            loginError.style.border = '1px solid #dc2626';
            loginError.style.color = '#dc2626';
        }
    }

    function hideError() {
        loginError.classList.add('hidden');
    }

    // Step 1: Search
    async function searchNames() {
        const lastName = normalizeLastName(lastNameInput.value.trim());
        if (!lastName) {
            showError('Please enter your last name');
            return;
        }

        btnSearch.textContent = 'Searching...';
        btnSearch.disabled = true;
        hideError();

        try {
            const data = await searchMembers(lastName);
            const matches = data.matches || [];

            btnSearch.textContent = 'Search';
            btnSearch.disabled = false;

            if (data.error) {
                showError(data.error);
                return;
            }

            if (matches.length === 0) {
                showError('Name not found. Check spelling or contact admin.');
                return;
            }

            nameSelect.innerHTML = '<option value="">-- Pick your name --</option>';
matches.forEach(item => {
    let firstName;
    let lastName;
    if (typeof item === 'string') {
        firstName = item;
        lastName = '';
    } else if (item && typeof item === 'object') {
        firstName = item.firstName || '';
        lastName = item.lastName || '';
    } else {
        // fallback: treat as string
        firstName = String(item);
        lastName = '';
    }
    if (!firstName) return;
    const opt = document.createElement('option');
    opt.value = firstName; // send as first name to backend
    opt.textContent = firstName;
    opt.dataset.lastName = lastName; // store for reference if needed
    nameSelect.appendChild(opt);
});

            document.getElementById('nameSelectionSection').classList.remove('hidden');
            selectedLastName = lastName;
            nameSelect.focus();            document.getElementById('loginSubtitle').textContent = 'Select your name from the list.';
            document.getElementById('lastNameLabel').textContent = 'Last Name (' + escapeHtml(lastName) + ')';
            document.getElementById('nameSelectLabel').textContent = 'Select Your Name';
            btnSearch.textContent = 'Search Another';
            updateProgress(2);
            scrollToCenter(document.getElementById('nameSelectionSection'));
        } catch (err) {
            btnSearch.textContent = 'Search';
            btnSearch.disabled = false;
            showError('Unable to search. Please try again.');
        }
    }

    // Step 2: Name selected
    nameSelect.addEventListener('change', async () => {
        const name = nameSelect.value;
        if (!name) {
            document.getElementById('pinSection').classList.add('hidden');
            document.getElementById('pinButtonArea').classList.add('hidden');
            return;
        }

        selectedFullName = name;
        document.getElementById('pinSection').classList.remove('hidden');
        pinInput.value = '';
        pinInput.focus();
        btnLogin.textContent = 'Checking...';
        btnLogin.disabled = true;
        document.getElementById('pinButtonArea').classList.remove('hidden');
        hideError();        document.getElementById('loginSubtitle').textContent = 'Enter your 4-digit PIN to secure your account.';
        document.getElementById('nameSelectLabel').textContent = 'Name Selected: ' + escapeHtml(name);
        updateProgress(3);
        scrollToCenter(document.getElementById('pinSection'));
        scrollToCenter(document.getElementById('pinButtonArea'));

        try {
            const data = await checkPinStatus(name, selectedLastName);
            isFirstTime = !data.hasPin;
            if (isFirstTime) {
                btnLogin.innerHTML = '<span style="color:#16a34a;">✅</span> Create Your PIN';
                showError('First time! Create a 4-digit PIN to secure your account.', true);
            } else {
                btnLogin.textContent = 'Login';
                loginError.classList.add('hidden');
            }
            btnLogin.disabled = false;
        } catch (err) {
            btnLogin.textContent = 'Login';
            btnLogin.disabled = false;
        }
    });

    // Search events
    btnSearch.addEventListener('click', searchNames);
    lastNameInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') searchNames();
    });

    // PIN toggle
    const togglePin = () => {
        pinInput.type = pinInput.type === 'password' ? 'text' : 'password';
    };
    document.getElementById('btnTogglePin').addEventListener('mousedown', togglePin);
    document.getElementById('btnTogglePin').addEventListener('mouseup', () => { pinInput.type = 'password'; });
    document.getElementById('btnTogglePin').addEventListener('mouseleave', () => { pinInput.type = 'password'; });

    // Step 3: Login
    async function doLogin() {
        const pin = pinInput.value.trim();
        hideError();
        scrollToCenter(document.getElementById('pinButtonArea'));

        if (!pin || !/^\d{4}$/.test(pin)) {
            showError('PIN must be exactly 4 digits.');
            pinInput.value = '';
            pinInput.focus();
            return;
        }

        btnLogin.textContent = 'Verifying...';
        btnLogin.disabled = true;

        try {
            const result = await login(selectedFullName, selectedLastName, pin);
            if (!result.success) {
                btnLogin.textContent = isFirstTime ? 'Set PIN' : 'Login';
                btnLogin.disabled = false;
                if (result.error.includes('Locked out')) {
                    renderLockoutScreen();
                    return;
                }
                showError(result.error);
                pinInput.value = '';
                pinInput.focus();
                return;
            }

            showToast(result.isFirstTime ? 'PIN set! Welcome!' : 'Welcome back!', 'success');
            sessionStorage.setItem('rolc_just_logged_in', '1');
            navigateToHome();
        } catch (err) {
            btnLogin.textContent = isFirstTime ? 'Set PIN' : 'Login';
            btnLogin.disabled = false;
            showError('Unable to verify. Please try again.');
        }
    }

    btnLogin.addEventListener('click', doLogin);
    pinInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') doLogin();
    });

    lastNameInput.focus();
    scrollToCenter(document.getElementById('lastName'));
}

// ============================================
// Setup Quick Login Handlers
// ============================================
function setupQuickLoginHandlers(savedName) {
    const pinInput = document.getElementById('qlPinInput');
    const submitBtn = document.getElementById('qlSubmit');
    const errorEl = document.getElementById('qlError');
    const toggleBtn = document.getElementById('qlTogglePin');
    const fullLoginLink = document.getElementById('qlUseFullLogin');

    let pinVisible = false;
    toggleBtn.addEventListener('click', () => {
        pinVisible = !pinVisible;
        pinInput.type = pinVisible ? 'text' : 'password';
        toggleBtn.innerHTML = pinVisible ? '👁' : '👁';
    });

    pinInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') submitBtn.click();
    });

    fullLoginLink.addEventListener('click', (e) => {
        e.preventDefault();
        localStorage.removeItem('rolc_token');
        localStorage.removeItem('rolc_name');
        renderFullLogin();
    });

    submitBtn.addEventListener('click', async () => {
        const pin = pinInput.value.trim();
        if (!/^\d{4}$/.test(pin)) {
            errorEl.textContent = 'PIN must be exactly 4 digits.';
            errorEl.classList.remove('hidden');
            return;
        }

        errorEl.classList.add('hidden');
        submitBtn.textContent = 'Logging in...';
        submitBtn.disabled = true;
        pinInput.disabled = true;

        try {
            const result = await quickLogin(pin);
            if (!result.success) {
                errorEl.textContent = result.error;
                errorEl.classList.remove('hidden');
                submitBtn.textContent = 'Login';
                submitBtn.disabled = false;
                pinInput.disabled = false;
                pinInput.value = '';
                pinInput.focus();
                return;
            }

            showToast('Welcome back, ' + escapeHtml(savedName) + '!', 'success');
            sessionStorage.setItem('rolc_just_logged_in', '1');
            navigateToHome();
        } catch (err) {
            errorEl.textContent = 'Unable to connect. Please try again.';
            errorEl.classList.remove('hidden');
            submitBtn.textContent = 'Login';
            submitBtn.disabled = false;
            pinInput.disabled = false;
        }
    });

    pinInput.focus();
    scrollToCenter(document.getElementById('qlPinInput'));
}

// ============================================
// Lockout Screen
// ============================================
function renderLockoutScreen() {
    setContent(`
        <div class="card">
            <div class="error-box">
                <div class="error-icon">🔒</div>
                <p class="error-text">Locked out. Contact your admin.</p>
                <button class="btn btn-secondary mt-1" id="btnLockoutBack">Back</button>
            </div>
        </div>
    `);
    document.getElementById('btnLockoutBack').addEventListener('click', renderFullLogin);
}

// ============================================
// Navigation helper (removed - using import from routes.js)
// ============================================






